import { useEffect, useMemo, useState } from "react";
import { WebsiteSettings } from "../../../models/WebsiteModels";
import { resolveImageUrl } from "../../../utils/ImageUrl";
import "./WebsiteModal.css";

interface WebsiteSettingsModalProps {
  isOpen: boolean;
  settings: WebsiteSettings | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (form: WebsiteSettingsFormValues) => Promise<void>;
}

export interface WebsiteSettingsFormValues {
  themeNumber: string;
  brandColor: string;
  isActive: boolean;
  appointmentImageFile: File | null;
  taglineImageFile: File | null;
}

const IMAGE_MAX_BYTES = 500 * 1024;

const fromSettings = (settings: WebsiteSettings | null): WebsiteSettingsFormValues => ({
  themeNumber: String(settings?.themeNumber ?? 1),
  brandColor: settings?.brandColor ?? "#0d9488",
  isActive: settings?.isActive ?? true,
  appointmentImageFile: null,
  taglineImageFile: null,
});

const WebsiteSettingsModal = ({
  isOpen,
  settings,
  submitting,
  onClose,
  onSubmit,
}: WebsiteSettingsModalProps) => {
  const [form, setForm] = useState<WebsiteSettingsFormValues>(() =>
    fromSettings(settings),
  );
  const [fileError, setFileError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setFileError(null);
    setForm(fromSettings(settings));
  }, [isOpen, settings]);

  const appointmentPreview = useMemo(() => {
    if (form.appointmentImageFile) return URL.createObjectURL(form.appointmentImageFile);
    return settings?.appointmentImageUrl
      ? resolveImageUrl(settings.appointmentImageUrl)
      : undefined;
  }, [form.appointmentImageFile, settings?.appointmentImageUrl]);

  const taglinePreview = useMemo(() => {
    if (form.taglineImageFile) return URL.createObjectURL(form.taglineImageFile);
    return settings?.taglineImageUrl
      ? resolveImageUrl(settings.taglineImageUrl)
      : undefined;
  }, [form.taglineImageFile, settings?.taglineImageUrl]);

  useEffect(() => {
    const urls = [appointmentPreview, taglinePreview].filter(
      (url): url is string => Boolean(url && url.startsWith("blob:")),
    );
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [appointmentPreview, taglinePreview]);

  if (!isOpen) return null;

  const setField = <K extends keyof WebsiteSettingsFormValues>(
    key: K,
    value: WebsiteSettingsFormValues[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleImageChange = (
    key: "appointmentImageFile" | "taglineImageFile",
    file: File | null,
  ) => {
    setField(key, file);
    if (file && file.size > IMAGE_MAX_BYTES) {
      setFileError(
        `Image must be 500 KB or less (selected file is ${Math.round(file.size / 1024)} KB).`,
      );
    } else {
      setFileError(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (
      form.appointmentImageFile &&
      form.appointmentImageFile.size > IMAGE_MAX_BYTES
    ) {
      setFileError(
        `Image must be 500 KB or less (selected file is ${Math.round(form.appointmentImageFile.size / 1024)} KB).`,
      );
      return;
    }
    if (
      form.taglineImageFile &&
      form.taglineImageFile.size > IMAGE_MAX_BYTES
    ) {
      setFileError(
        `Image must be 500 KB or less (selected file is ${Math.round(form.taglineImageFile.size / 1024)} KB).`,
      );
      return;
    }
    onSubmit(form);
  };

  return (
    <div className="wbm-overlay">
      <div className="wbm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="wbm-modal__header">
          <h3>Website Settings</h3>
          <button
            className="wbm-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form className="wbm-modal__body" onSubmit={handleSubmit}>
          <div className="wbm-form-group">
            <label htmlFor="wbs-theme">Theme Number</label>
            <input
              id="wbs-theme"
              type="number"
              min={1}
              value={form.themeNumber}
              onChange={(e) => setField("themeNumber", e.target.value)}
            />
          </div>

          <div className="wbm-form-group">
            <label htmlFor="wbs-brand">Brand Color</label>
            <div className="wbs-color-row">
              <input
                id="wbs-brand"
                className="wbs-color-btn"
                type="color"
                value={form.brandColor}
                onChange={(e) => setField("brandColor", e.target.value)}
                title="Choose brand color"
              />
              <input
                type="text"
                value={form.brandColor}
                onChange={(e) => setField("brandColor", e.target.value)}
                placeholder="#0d9488"
                aria-label="Brand color hex value"
              />
            </div>
          </div>

          <div className="wbm-form-group">
            <label htmlFor="wbs-appointment">Appointment Image</label>
            {appointmentPreview && (
              <img
                className="wbm-preview"
                src={appointmentPreview}
                alt="Appointment image preview"
              />
            )}
            <input
              id="wbs-appointment"
              type="file"
              accept="image/*"
              onChange={(e) =>
                handleImageChange("appointmentImageFile", e.target.files?.[0] ?? null)
              }
            />
            <p className="wbm-hint">
              Shown on the right side of the appointment card beside the booking
              form. Use a 3:4 portrait — 900×1200 min / 1200×1600 ideal. Max
              500 KB.
            </p>
          </div>

          <div className="wbm-form-group">
            <label htmlFor="wbs-tagline">Tagline Image</label>
            {taglinePreview && (
              <img
                className="wbm-preview"
                src={taglinePreview}
                alt="Tagline image preview"
              />
            )}
            <input
              id="wbs-tagline"
              type="file"
              accept="image/*"
              onChange={(e) =>
                handleImageChange("taglineImageFile", e.target.files?.[0] ?? null)
              }
            />
            <p className="wbm-hint">
              Shown beside the tagline text as a wide landscape. Use 16:9 —{" "}
              1280×720 min / 1920×1080 ideal. Max 500 KB.
            </p>
          </div>

          <div className="wbm-form-group">
            <label className="wbm-form-check">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setField("isActive", e.target.checked)}
              />
              Active
            </label>
          </div>

          {fileError && <p className="wbm-error">{fileError}</p>}

          <div className="wbm-modal__actions">
            <button
              type="button"
              className="wbm-btn wbm-btn--secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="wbm-btn wbm-btn--primary"
              disabled={submitting || !!fileError}
            >
              {submitting ? "Saving..." : "Save Settings"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WebsiteSettingsModal;